import {
  TFasterPayment,
  TFasterPaymentStatus,
} from '../../../../../api/src';
import {
  Badge,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import {
  NoData,
  ScrollBarWithFade,
} from '@payler/ui-components';
import { TableSkeleton } from '../../components/TableSkeleton/TableSkeleton';
import { PaymentDatesCell } from '../PaymentsTable/ReportDateCell';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDayjsFormat } from '@payler/utils';
import { TableFooter } from '../components/TableFooter';
import { tableStyleConfig } from '../tableStyleConfig';
import { GroupRow } from '../PaymentsTable/GroupRow';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import {
  useFasterPaymentsQuery,
  useFasterPaymentsQueryPagerProps,
} from '../../hooks/faster-payments/queries';
import { AmountWithCurrencyCell } from '../PaymentsTable/AmountWithCurrencyCell';
import { IdsCell } from './IdsCell';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../hooks/use-toasts';

export const FasterPaymentsTable = () => {
  const props = useFasterPaymentsQueryPagerProps();
  return (
    <>
      <React.Suspense fallback={null}>
        <TableWithData />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <TableFooter paginationProps={props} />
      </React.Suspense>
    </>
  );
};

const TableWithData = () => {
  const { status, data } = useFasterPaymentsQuery();
  const toasts = useToasts();

  switch (status) {
    case 'pending':
      return <TableSkeleton />;
    case 'error':
      toasts.error();
      return <NoData />;
    case 'success':
    default: {
      const empty = data.items.length === 0;
      if (empty) {
        return <NoData />;
      }
      return (
        <ScrollBarWithFade>
          <TableContainer position="relative">
            <Table styleConfig={tableStyleConfig}>
              <Header />
              <Tbody>
                {Object.entries(data.grouped)
                  .sort(([a], [b]) => -a.localeCompare(b))
                  .map(([group, rows]) => (
                    <Rows key={group} group={group} payments={rows} />
                  ))}
              </Tbody>
            </Table>
          </TableContainer>
        </ScrollBarWithFade>
      );
    }
  }
};

const Rows = ({
  group,
  payments,
}: {
  group: string;
  payments: TFasterPayment[];
}) => {
  const format = useDayjsFormat();
  return (
    <>
      <tr>
        <GroupRow colSpan={5}>{format(group, 'dddd DD MMMM')}</GroupRow>
      </tr>
      {payments.map((row) => (
        <Row data={row} key={row.id} />
      ))}
    </>
  );
};

const Row = ({ data }: { data: TFasterPayment }) => {
  const contractId = useURLContractId();
  const navigate = useNavigate();
  const location = useLocation();
  const handleClick = () => {
    if (contractId) {
      navigate(`/${contractId}/faster-payments/${data.id}${location.search}`);
    }
  };
  return (
    <Tr onClick={handleClick}>
      <Td>
        <PaymentDatesCell
          topDate={data.createdAt}
          bottomDate={data.finalStatusChangedAt}
        />
      </Td>
      <Td>
        <Badge variant={getFasterPaymentBadgeVariant(data.status)}>
          {data.statusDescription}
        </Badge>
      </Td>
      <Td>{data.processingName}</Td>
      <Td>
        <IdsCell orderId={data.orderId} id={data.id} />
      </Td>
      <Td textAlign="right">
        <AmountWithCurrencyCell
          amount={data.amount ?? 0}
          currencyCode={data.currencyCode}
          amountOfRefunds={0}
        />
      </Td>
    </Tr>
  );
};

export function getFasterPaymentBadgeVariant(status: TFasterPaymentStatus) {
  switch (status) {
    case 'Created':
    case 'Pending':
    case 'Returned':
      return 'yellow';
    case 'Completed':
      return 'green';
    case 'Failed':
      return 'red';
    default:
      return undefined;
  }
}

const Header = () => {
  const { t } = useTranslation(['tableHeader']);
  return (
    <Thead>
      <Tr>
        <Th>{t('tableHeader:period')}</Th>
        <Th>{t('tableHeader:status')}</Th>
        <Th>{t('tableHeader:processingName')}</Th>
        <Th textAlign="right">{t('tableHeader:idAndOrderId')}</Th>
        <Th textAlign="right">{t('tableHeader:amount')}</Th>
      </Tr>
    </Thead>
  );
};

export default FasterPaymentsTable;
